import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Small } from 'foundations-components/transitional/components/Small';
import { H2 } from 'foundations-components/transitional/components/H2';
import { H4 } from 'foundations-components/transitional/components/H4';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { ScrollingColumns } from 'foundations-components/transitional/components/ScrollingColumns';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { isOptionGroup } from 'UIComponents/types/OptionTypes';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'transmute/debounce';
import { OptionOrderInput } from './OptionOrderInput';
import OptionSelectionInput from './OptionSelectionInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const OptionConfigurationModal = ({
  closeModal,
  getInitialOptionPosition,
  getSelectedOptionLabel,
  getSubmitDisabledMessage = () => null,
  onSelectionChange = () => {},
  onSubmit,
  OptionFilter,
  OptionOrderToolbar,
  options,
  orderColumnLabelKey,
  OrderHelpText,
  SelectionColumnLabel,
  selectionLimit,
  Title,
  value: initialSelections,
  isLoading = false
}) => {
  const initialSelectedOptions = useMemo(() => {
    const childOptions = options.reduce((memo, option) => {
      const moreOptions = isOptionGroup(option) ? option.options : option;
      return /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */(
        memo.concat(moreOptions)
      );
    }, []);
    return initialSelections.reduce((memo, selection) => {
      const match = childOptions.find(option => option.value === selection);
      return match /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */ ? memo.concat(match) : memo;
    }, []);
  }, [initialSelections, options]);
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [searchTerm, setSearchTerm] = useState('');
  const handleSelectionChange = useCallback(newSelectedOptions => {
    setSelectedOptions(newSelectedOptions);
    onSelectionChange(newSelectedOptions.map(selection => selection.value));
  }, [onSelectionChange]);
  const handleSearchDebounced = useMemo(() => debounce(300, search => {
    setSearchTerm(search);
  }), []);
  const handleSearchChange = useCallback(({
    target: {
      value
    }
  }) => {
    handleSearchDebounced(value.toLowerCase());
  }, [handleSearchDebounced]);
  const SubmitDisabledMessage = useMemo(() => getSubmitDisabledMessage(selectedOptions.map(option => option.value)), [getSubmitDisabledMessage, selectedOptions]);
  return /*#__PURE__*/_jsxs(Modal, {
    width: 850,
    "data-test-id": "option-order-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: Title
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: closeModal
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(ScrollingColumns, {
        maxHeight: "425px",
        leftColumnHeader: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            marginBlockEnd: "0",
            children: SelectionColumnLabel
          }), /*#__PURE__*/_jsx(SearchInput, {
            onChange: handleSearchChange,
            placeholder: I18n.text('reporting-ui-components.option-config-modal.searchPlaceholder'),
            marginBlock: "100"
          }), OptionFilter]
        }),
        leftColumnContent: isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
          layout: "centered",
          size: "small",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-enablement.reportEditors.optionConfigurationModal.optionSelectionInput.loading"
          }),
          showLabel: true,
          marginBlockEnd: "600"
        }) : /*#__PURE__*/_jsx(OptionSelectionInput, {
          getInitialOptionPosition: getInitialOptionPosition,
          onSelectionChange: handleSelectionChange,
          options: options,
          searchTerm: searchTerm,
          selectedOptions: selectedOptions,
          selectionLimit: selectionLimit
        }),
        rightColumnHeader: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            marginBlockEnd: "0",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: orderColumnLabelKey,
              options: {
                count: selectedOptions.length,
                limit: selectionLimit
              }
            })
          }), /*#__PURE__*/_jsx(Small, {
            children: OrderHelpText
          })]
        }),
        rightColumnContent: /*#__PURE__*/_jsx(OptionOrderInput, {
          getSelectedOptionLabel: getSelectedOptionLabel,
          OptionOrderToolbar: OptionOrderToolbar,
          onSelectionChange: handleSelectionChange,
          selectedOptions: selectedOptions
        }),
        leftColumnMinWidth: "40%",
        rightColumnWidth: "60%"
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: SubmitDisabledMessage,
        disabled: !SubmitDisabledMessage,
        children: /*#__PURE__*/_jsx(Button, {
          "data-test-id": "apply-changes-button",
          disabled: !!SubmitDisabledMessage,
          use: "primary",
          onClick: () => {
            onSubmit(selectedOptions.map(selection => selection.value));
            closeModal();
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-ui-components.option-config-modal.apply"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: closeModal,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.option-config-modal.cancel"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: () => {
          handleSelectionChange([]);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.option-config-modal.clear"
        })
      })]
    })]
  });
};
export default OptionConfigurationModal;